import publicBasemaps from '../data/public-basemaps.json';
import { createPublicRasterBasemap, createPublicVectorBasemap } from './public-basemap-rendering.js';
import { prepareOfflineEditor } from './offline-bootstrap.js';
import { isSatelliteBasemap } from '../shared/satellite-access.js';
import { configureAzureAttributionRoute, getAzureAttributionHtml, updateAzureMapAttribution } from './azure-attribution.js';
import { installFeatureRecovery } from './lazy-feature-recovery.js';
installFeatureRecovery();
import { createLazyFeature } from './lazy-feature-loader.js';
const loadOlMapboxStyleModule = createLazyFeature(() => import('ol-mapbox-style'), { automaticRecovery: false });
const loadVectorTileLayerModule = createLazyFeature(() => import('./vector-basemap-layer.js'), { automaticRecovery: false });
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import ScaleLine from 'ol/control/ScaleLine.js';
import Zoom from 'ol/control/Zoom.js';
import { t } from './i18n.js';
import { defaults as controlDefaults } from 'ol/control/defaults.js';
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom.js';
import DragPan from 'ol/interaction/DragPan.js';
import DragZoom from 'ol/interaction/DragZoom.js';
import { defaults as interactionDefaults } from 'ol/interaction/defaults.js';
import { shiftKeyOnly } from 'ol/events/condition.js';
import TileLayer from 'ol/layer/Tile.js';
import LayerGroup from 'ol/layer/Group.js';
import type BaseLayer from 'ol/layer/Base.js';
import type TileSource from 'ol/source/Tile.js';
import type { SchemaInput } from '../shared/schema-input.js';
import { fromLonLat, get as getProjection } from 'ol/proj.js';
import OSM from 'ol/source/OSM.js';
import XYZ from 'ol/source/XYZ.js';
import { createXYZ } from 'ol/tilegrid.js';
import CircleStyle from 'ol/style/Circle.js';
import Fill from 'ol/style/Fill.js';
import Icon from 'ol/style/Icon.js';
import Stroke from 'ol/style/Stroke.js';
import Style from 'ol/style/Style.js';
import { TrackpadPinchZoom } from './trackpad-pinch-zoom.js';
import { ensureSegmenter } from './segmenter.js';

const DEFAULT_CENTER = [-74.0060, 40.7128];
const DEFAULT_ZOOM = 13;
const DEFAULT_MIN_ZOOM = 2;
const BASE_LAYER_CACHE_SIZE = 512;
const AZURE_SATELLITE_TILE_SIZE = 256;
const AZURE_SATELLITE_MAX_ZOOM = 19;
const satelliteTileGrid = createXYZ({ minZoom: 1, maxZoom: AZURE_SATELLITE_MAX_ZOOM, tileSize: AZURE_SATELLITE_TILE_SIZE });
const TILES_DEFERRED_PROPERTY = 'tilesDeferred';
const BASEMAP_READY_PROPERTY = 'basemapReady';
const DRAWING_LIGHT_STYLE_URL = publicBasemaps.carto.styleUrl;
export const DEFAULT_BASEMAP_ID = 'carto';
let azureSatelliteTileUrl = '';

export type BasemapLayer = LayerGroup;

interface BasemapContent {
  layer: BaseLayer;
  ready: Promise<void>;
}

function azureSatelliteUrl() {
  if (!azureSatelliteTileUrl) throw new Error('Azure Maps access is not configured');
  return azureSatelliteTileUrl;
}

export function configureAzureSatelliteTileUrl(tileUrl: string) {
  if (!tileUrl.startsWith('/api/azure-satellite?')) throw new Error('Azure Maps requires the private satellite tile route');
  azureSatelliteTileUrl = tileUrl;
  configureAzureAttributionRoute(tileUrl);
}

export function azureHybridLabelsUrl(): string {
  return `${azureSatelliteUrl()}&layer=labels`;
}

function createHybridLabelsSource(): XYZ {
  return new XYZ({ url: azureHybridLabelsUrl(), crossOrigin: 'anonymous', tileGrid: satelliteTileGrid, attributions: () => getAzureAttributionHtml() });
}

export const BASEMAPS = Object.freeze({
  landscape: {
    id: 'landscape', label: 'Landscape',
    globe: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', subdomains: ['a', 'b', 'c'], maximumLevel: 19, credit: 'OpenFreeMap · © OpenMapTiles · © OpenStreetMap contributors' },
    styleUrl: publicBasemaps.landscape.styleUrl,
    attributionHtml: publicBasemaps.landscape.attributionHtml,
  },
  carto: {
    id: 'carto',
    label: 'Default',
    globe: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      subdomains: ['a', 'b', 'c'],
      maximumLevel: 19,
      credit: 'OpenFreeMap · © OpenMapTiles · © OpenStreetMap contributors',
    },
    styleUrl: DRAWING_LIGHT_STYLE_URL,
    attributionHtml: publicBasemaps.carto.attributionHtml
  },
  dark: {
    id: 'dark',
    label: 'Dark',
    globe: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      subdomains: ['a', 'b', 'c'],
      maximumLevel: 19,
      credit: 'OpenFreeMap · © OpenMapTiles · © OpenStreetMap contributors',
    },
    styleUrl: publicBasemaps.dark.styleUrl,
    attributionHtml: publicBasemaps.dark.attributionHtml
  },
  osm: {
    id: 'osm',
    label: 'Detailed',
    globe: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      subdomains: ['a', 'b', 'c'],
      maximumLevel: 19,
      credit: '© OpenStreetMap contributors',
    },
    createSource() {
      return new OSM({
        crossOrigin: 'anonymous',
        attributions: publicBasemaps.osm.attributionHtml
      });
    },
    attributionHtml: publicBasemaps.osm.attributionHtml
  },
  opentopo: {
    id: 'opentopo',
    label: 'OpenTopoMap',
    globe: {
      url: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
      maximumLevel: 17,
      credit: '© OpenStreetMap contributors · SRTM · OpenTopoMap',
    },
    createSource() {
      return new XYZ({
        url: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
        crossOrigin: 'anonymous',
        maxZoom: 17,
        attributions: 'Map data: © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors, <a href="https://viewfinderpanoramas.org" target="_blank" rel="noopener">SRTM</a> | Map style: © <a href="https://opentopomap.org" target="_blank" rel="noopener">OpenTopoMap</a> (CC-BY-SA)'
      });
    },
    attributionHtml: 'Map data: © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors, <a href="https://viewfinderpanoramas.org" target="_blank" rel="noopener">SRTM</a> | Map style: © <a href="https://opentopomap.org" target="_blank" rel="noopener">OpenTopoMap</a> (CC-BY-SA)'
  },
  satellite: {
    id: 'satellite',
    label: 'Satellite',
    globe: {
      get url() { return azureSatelliteUrl(); },
      maximumLevel: AZURE_SATELLITE_MAX_ZOOM,
      tileWidth: AZURE_SATELLITE_TILE_SIZE,
      tileHeight: AZURE_SATELLITE_TILE_SIZE,
      get credit() { return getAzureAttributionHtml(); },
    },
    createSource() {
      return new XYZ({
        url: azureSatelliteUrl(),
        crossOrigin: 'anonymous',
        tileGrid: satelliteTileGrid,
        tilePixelRatio: 1,
        attributions: () => getAzureAttributionHtml()
      });
    },
    get attributionHtml() { return getAzureAttributionHtml(); }
  },
  hybrid: {
    id: 'hybrid',
    label: 'Hybrid',
    globe: {
      get url() { return azureSatelliteUrl(); },
      maximumLevel: AZURE_SATELLITE_MAX_ZOOM,
      tileWidth: AZURE_SATELLITE_TILE_SIZE,
      tileHeight: AZURE_SATELLITE_TILE_SIZE,
      get credit() { return getAzureAttributionHtml(); },
    },
    createSource() {
      return new XYZ({
        url: azureSatelliteUrl(),
        crossOrigin: 'anonymous',
        tileGrid: satelliteTileGrid,
        tilePixelRatio: 1,
        attributions: () => getAzureAttributionHtml()
      });
    },
    get attributionHtml() { return getAzureAttributionHtml(); }
  }
});

const markerSvgDataUrl = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42">
    <path fill="#e63946" stroke="#ffffff" stroke-width="2" d="M15 1C8.373 1 3 6.373 3 13c0 9.271 12 28 12 28s12-18.729 12-28C27 6.373 21.627 1 15 1Z"/>
    <circle cx="15" cy="13" r="5" fill="#ffffff"/>
  </svg>
`)}`;

export function createDefaultMapView({ constrainOnlyCenter = false } = {}) {
  return new View({
    center: fromLonLat(DEFAULT_CENTER),
    constrainOnlyCenter,
    extent: constrainOnlyCenter ? getProjection('EPSG:3857')?.getExtent() : undefined,
    zoom: DEFAULT_ZOOM,
    showFullExtent: true,
    minZoom: DEFAULT_MIN_ZOOM
  });
}

export function createDefaultMapInteractions() {
  return interactionDefaults({
    mouseWheelZoom: false,
    shiftDragZoom: false,
  }).extend([
    new DragZoom({
      condition: (event) => shiftKeyOnly(event)
        && event.map.getTargetElement()?.classList.contains('drawing-mode') !== true,
    }),
    // The default drag interaction is disabled while drawing, so retain a
    // separate interaction that only begins when the second touch arrives.
    // This lets two fingers move the map without ever treating a one-finger
    // drawing gesture as a pan.
    new DragPan({
      condition: (event) => {
        // SAFETY: The mapping library contract supplies this concrete map value type.
        const originalEvent = event.originalEvent as PointerEvent | undefined;
        return originalEvent?.pointerType === 'touch'
          && originalEvent.isPrimary === false
          && event.map.getTargetElement()?.classList.contains('drawing-mode') === true;
      },
    }),
    new MouseWheelZoom(),
    new TrackpadPinchZoom(),
  ]);
}

export function createDefaultMapControls(target?: string | HTMLElement) {
  const zoomOptions = {
    zoomInTipLabel: t('map_controls.zoom_in'),
    zoomOutTipLabel: t('map_controls.zoom_out'),
  };
  return controlDefaults({
    rotate: false,
    attribution: false, // Static attribution in HTML; dynamic one would re-gate LCP behind JS
    zoom: !target,
    zoomOptions,
  }).extend([
    ...(target ? [new Zoom({ target, ...zoomOptions })] : []),
    new ScaleLine({
      units: 'metric',
      minWidth: 48,
      className: 'ol-scale-line metric-scale',
      target
    }),
    new ScaleLine({
      units: 'imperial',
      minWidth: 48,
      className: 'ol-scale-line imperial-scale',
      target
    })
  ]);
}

function isBasemapId(id: string): id is keyof typeof BASEMAPS {
  return Object.prototype.hasOwnProperty.call(BASEMAPS, id);
}

export function normalizeBasemapId(id: string): keyof typeof BASEMAPS {
  return isBasemapId(id) ? id : DEFAULT_BASEMAP_ID;
}

export function getBasemapDefinition(id: string) {
  return BASEMAPS[normalizeBasemapId(id)];
}

function createRasterBasemapLayer(basemapId: 'osm' | 'opentopo' | 'satellite'): TileLayer {
  if (basemapId === 'osm') return createPublicRasterBasemap(publicBasemaps.osm.attributionHtml);
  return new TileLayer({
    cacheSize: BASE_LAYER_CACHE_SIZE,
    source: BASEMAPS[basemapId].createSource(),
  });
}

function createPositronLayer(basemapId: 'carto' | 'landscape' | 'dark'): BasemapContent {
  return createPublicVectorBasemap(publicBasemaps[basemapId], publicBasemaps.osm, async () => {
    const [{ applyStyle, applyBackground }, { createVectorBasemapLayer }] = await Promise.all([loadOlMapboxStyleModule(), loadVectorTileLayerModule(), ensureSegmenter(), prepareOfflineEditor()]);
    return { applyStyle, applyBackground, createVectorBasemapLayer };
  }, disposeAfterBasemapRender);
}

function createBasemapContent(basemapId: keyof typeof BASEMAPS): BasemapContent {
  if (basemapId === 'hybrid') {
    return { layer: new LayerGroup({ layers: [createRasterBasemapLayer('satellite'), new TileLayer({ cacheSize: BASE_LAYER_CACHE_SIZE, source: createHybridLabelsSource() })] }), ready: Promise.resolve() };
  }
  if (basemapId === 'carto' || basemapId === 'landscape' || basemapId === 'dark') return createPositronLayer(basemapId);
  return { layer: createRasterBasemapLayer(basemapId), ready: Promise.resolve() };
}

function disposeAfterBasemapRender(layers: BaseLayer[]): () => void {
  const previous = [...layers];
  const maps = new Set(previous.flatMap(layer => layer.getLayersArray().map(child => child.getMapInternal())).filter(map => map !== null && map !== undefined));
  return () => {
    const dispose = () => previous.forEach(layer => layer.dispose());
    if (!maps.size) { dispose(); return; }
    let remaining = maps.size;
    for (const map of maps) map.once('postrender', () => { if (--remaining === 0) dispose(); });
  };
}

function replaceBasemapContent(layer: BasemapLayer, basemapId: keyof typeof BASEMAPS): void {
  const dispose = disposeAfterBasemapRender(layer.getLayers().getArray());
  const next = createBasemapContent(basemapId);
  layer.getLayers().clear();
  layer.getLayers().push(next.layer);
  layer.set(BASEMAP_READY_PROPERTY, next.ready);
  dispose();
}

export function createBaseLayer(
  basemapId = DEFAULT_BASEMAP_ID,
  { deferTiles = false }: { deferTiles?: boolean } = {},
) {
  const normalizedId = normalizeBasemapId(basemapId);
  const layer = new LayerGroup({
    properties: {
      basemapId: normalizedId,
      [TILES_DEFERRED_PROPERTY]: deferTiles,
      [BASEMAP_READY_PROPERTY]: Promise.resolve(),
    },
  });
  if (!deferTiles) replaceBasemapContent(layer, normalizedId);
  return layer;
}

export function setBaseLayerBasemap(layer: BasemapLayer, basemapId: string) {
  const normalizedId = normalizeBasemapId(basemapId);
  layer.set('basemapId', normalizedId);
  if (layer.get(TILES_DEFERRED_PROPERTY) !== true) replaceBasemapContent(layer, normalizedId);
  return normalizedId;
}

export function activateBaseLayer(layer: BasemapLayer): void {
  if (layer.get(TILES_DEFERRED_PROPERTY) !== true) return;
  layer.set(TILES_DEFERRED_PROPERTY, false);
  const basemapId = normalizeBasemapId(String(layer.get('basemapId')));
  replaceBasemapContent(layer, basemapId);
}

export function getBaseLayerTileSources(layer: BasemapLayer): TileSource[] {
  const first = layer.getLayers().item(0);
  const layers = first instanceof LayerGroup ? first.getLayers().getArray() : [first];
  return layers.flatMap(child => child instanceof TileLayer && child.getSource() ? [child.getSource()!] : []);
}

export function getBaseLayerTileSource(layer: BasemapLayer): TileSource | null {
  return getBaseLayerTileSources(layer)[0] ?? null;
}

export function whenBaseLayerReady(layer: BasemapLayer): Promise<void> {
  const ready = layer.get(BASEMAP_READY_PROPERTY);
  return ready instanceof Promise ? ready : Promise.resolve();
}

export function getBasemapAttributionHtml(basemapId: string) {
  return BASEMAPS[normalizeBasemapId(basemapId)].attributionHtml;
}

export function getBasemapBackgroundColor(basemapId: string): string {
  if (basemapId === 'dark') return '#18232d';
  const normalized = normalizeBasemapId(basemapId);
  if (normalized === 'landscape' || normalized === 'carto') return publicBasemaps[normalized].background;
  return '#f8fafc';
}

export function createBaseMap({
  target = 'map',
  view = createDefaultMapView(),
  interactions = createDefaultMapInteractions(),
  controls = createDefaultMapControls(),
  basemapId = DEFAULT_BASEMAP_ID,
  deferTiles = false,
} = {}) {
  const baseLayer = createBaseLayer(basemapId, { deferTiles });
  bindBasemapZoomLimit(view, baseLayer);
  const map = new Map({
    target,
    layers: [
      baseLayer
    ],
    view,
    interactions,
    controls
  });
  const updateCredits = () => {
    if (isSatelliteBasemap(baseLayer.get('basemapId'))) void updateAzureMapAttribution(map).catch(() => {});
  };
  map.on('moveend', updateCredits);
  baseLayer.on('propertychange', updateCredits);
  return map;
}

export function getSatelliteViewMaxZoom(): number {
  return globalThis.window?.matchMedia('(max-width: 768px)').matches ? 21 : 20;
}

export function bindBasemapZoomLimit(view: View, layer: BasemapLayer): void {
  const originalMaxZoom = view.getMaxZoom();
  // SAFETY: The initialized view and tile grid provide a numeric resolution and zoom.
  const satelliteMaxZoom = view.getZoomForResolution(satelliteTileGrid.getResolution(AZURE_SATELLITE_MAX_ZOOM) / 2 ** (getSatelliteViewMaxZoom() - AZURE_SATELLITE_MAX_ZOOM)) as number;
  const update = () => view.setMaxZoom(isSatelliteBasemap(layer.get('basemapId'))
    ? Math.min(originalMaxZoom, satelliteMaxZoom)
    : originalMaxZoom);
  layer.on('propertychange', (event) => {
    if (event.key === 'basemapId') update();
  });
  update();
}

export function createSearchLocationStyles() {
  const searchMarkerStyle = new Style({
    image: new Icon({
      src: markerSvgDataUrl,
      anchor: [0.5, 1],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
      width: 30,
      height: 42
    })
  });
  const locationMarkerStyle = new Style({
    image: new CircleStyle({
      radius: 8,
      fill: new Fill({ color: '#3b82f6' }),
      stroke: new Stroke({ color: '#ffffff', width: 3 })
    })
  });
  const locationAccuracyStyle = new Style({
    fill: new Fill({ color: 'rgba(59, 130, 246, 0.15)' }),
    stroke: new Stroke({ color: '#3b82f6', width: 1 })
  });
  const searchGeometryStyle = new Style({
    fill: new Fill({ color: 'rgba(37, 99, 235, 0.16)' }),
    stroke: new Stroke({ color: '#2563eb', width: 3 }),
  });

  return {
    searchMarkerStyle,
    searchGeometryStyle,
    locationMarkerStyle,
    locationAccuracyStyle,
    styleForFeature(feature: { get: (key: string) => SchemaInput }) {
      const kind = feature.get('kind');
      if (kind === 'search_marker') return searchMarkerStyle;
      if (kind === 'search_geometry') return searchGeometryStyle;
      if (kind === 'location_marker') return locationMarkerStyle;
      if (kind === 'location_accuracy') return locationAccuracyStyle;
      return null;
    }
  };
}
